import { applyMutator, GameMutator, GameState, isGameSolved, isValidWord, Letter as LetterType, MutationResult, Word as WordType } from "../game/game";
import { Mutator } from "../game/mutators";
import { motion, AnimatePresence } from 'framer-motion';

import './game.less'
import React, { CSSProperties, ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { LongPressable } from "./longPressable";
import { useAutoAdjustFontSize } from "../hooks/useAutoAdjustFontSize";


interface GameProps {
    gameState: GameState,
    onMutatorSelected: (mutator: GameMutator)=>void
    information: (MutationResult|null)[]
    memory: Mutator[]
    disableInput: boolean
    flippingMutators: number[]
    onUndo: ()=>void
    moves: number|null
    description: ReactNode
    style?: CSSProperties
    className?: string
    footer? : ReactNode
}

function useDebouncedState<T>(initialValue: T, delay: number, throttleTime: number): [T, React.Dispatch<React.SetStateAction<T>>] {
    const [value, setValue] = useState(initialValue);
    const [debouncedValue, setDebouncedValue] = useState(initialValue);
    const lastChangeTime = useRef(Date.now());

    useLayoutEffect(() => { //Layout effect to prevent flickering
        const now = Date.now();
        const timeSinceLastChange = now - lastChangeTime.current;

        if (value === debouncedValue) return;
        if (value === initialValue) {
            setDebouncedValue(initialValue);
            return;
        }

        if (timeSinceLastChange < throttleTime) {
            const handler = setTimeout(() => {
                setDebouncedValue(value);
                lastChangeTime.current = Date.now();
            }, delay);
            return () => {
                clearTimeout(handler);
            };
        } else {
            setDebouncedValue(value);
            lastChangeTime.current = Date.now();
        }
    }, [value, delay, throttleTime]);

    return [debouncedValue, setValue];
}


export function GameStateComp(props:GameProps){
    const {className, moves,style, gameState: {words, customWords, gameMutators: mutators, mutationStack, solutions}, disableInput, onMutatorSelected, flippingMutators, description} = props

    const [previewMutator, setPreviewMutator] = useDebouncedState<Mutator | null>(null, 300,250);
    const previewState = useMemo(() => {
        return previewMutator ? applyMutator(props.gameState, previewMutator)[0] : null;
    }, [previewMutator?.name, props.gameState]);
    
    const ref = useRef<HTMLDivElement>(null);
    const zoomOut = ()=>{
        if(!ref.current) return;
        const windowHeight = window.innerHeight
        const gameHeight = ref.current.clientHeight + 10
        const scale = windowHeight / gameHeight;
        (ref.current.style as any).zoom = `${scale*100}%`
    }
    useEffect(()=>{
        zoomOut()
        window.addEventListener('resize', zoomOut)
        return ()=>window.removeEventListener('resize', zoomOut)
    }, [])

    const solutionLength = solutions[0].mutators.length;
    return <div ref={ref} className={`game ${className}`} style={style}>
        
    <h1>Mutatle</h1>
    <p>{description}</p>
    {moves !== null && <p><b>{moves} moves </b><i>(par 8)</i></p>}
        <div className='words'>
            {words.map((w, i) => <Word 
                bouncing={isGameSolved(words, customWords??[])} key={w.correctValue} word={w} previewStateWord={previewState && previewState.words[i]}
                gamestate={props.gameState}
             />)}
        </div>
        <div className='mutator-stack'>
            
        </div>
        <div className='mutators'>
            {solutions[0].mutators.map((m, i)=><MutatorSlot 
                flipping={flippingMutators.includes(mutators.findIndex(m=>m.mutator === props.memory[i]))}  key={m.name} solutionLength={solutionLength} index={i} memory={props.memory[i]} />)}
            {mutators.map((m, i)=> <MutatorItem
                key={m.mutator.name}
                index={i}
                mutator={m}
                information={props.information[i] || null}
                flipping={flippingMutators.includes(i)}
                mutationStack={mutationStack}
                solutionLength={solutionLength}
                disableInput={disableInput}
                setPreviewMutator={setPreviewMutator}
                onMutatorSelected={onMutatorSelected}
                onUndo={props.onUndo}
            />)}
        </div>
        {props.footer}
    </div>
}
export function Word(props: { word: WordType, previewStateWord: WordType | null, bouncing: boolean, gamestate: GameState }) {
    const { word, previewStateWord, bouncing, gamestate } = props
    const isCorrect = isValidWord(word, gamestate.customWords??[])
    const [allLetters, anyGhostLetters] = useMemo(() => {
        if (!previewStateWord) return [word.value, false];
    
        if(previewStateWord.value.length > word.value.length){
            return [previewStateWord.value, true]
        }
        return [word.value, false]
      }, [word, previewStateWord]);
    const anyRemovedLetters = previewStateWord ? word.value.length > previewStateWord.value.length : false;
    return (
        <div className={`word ${isCorrect ? 'word--correct' : ''}`}>
            <AnimatePresence initial={false} mode="popLayout">
                {allLetters.map((letter, index) => (
                    
                <motion.span
                    className='letter-wrapper'
                    key={letter.key}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{
                        opacity: 0,
                        y: 20,
                    }}
                    transition={{ duration: 0.2 }}
                    layout
                >
                    <Letter
                        letter={letter}
                        index={index}
                        isGhost={!word.value.some(l => l.key === letter.key)}
                        previewStateWord={previewStateWord}
                        bouncing={bouncing}
                        anyRemovedLetters={anyRemovedLetters}
                        anyGhostLetters={anyGhostLetters}
                    />
                    </motion.span>
                ))}
            </AnimatePresence>
        </div>
    )
}

export function Letter(props: {
    letter: LetterType,
    index: number,
    previewStateWord: WordType | null,
    isGhost: boolean,
    bouncing: boolean,
    anyRemovedLetters: boolean,
    anyGhostLetters: boolean
}) {
    const { letter, index, previewStateWord, isGhost, bouncing, anyRemovedLetters, anyGhostLetters } = props;
    const [value, setValue] = useState(props.letter.value)
    const [wasChanged, setWasChanged] = useState(false)
    useEffect(() => {
        if(value === letter.value) return;
        setWasChanged(true)
        setTimeout(()=>{
            setValue(letter.value)
            setWasChanged(false)
        }, 300)
    }, [letter.value, value])

    const [wasGhost, setWasGhost] = useState(isGhost);

    useEffect(() => {
        if (isGhost) {
            setWasGhost(true);
        }
    }, [isGhost]);

    const previewLetter = previewStateWord?.value.find(l => l.key === letter.key)
    const willBeRemoved = previewStateWord && !previewLetter 
    const previewIndex = previewStateWord?.value.findIndex(l => l.key === letter.key)
    const willChangeValue = previewStateWord && previewLetter && previewLetter.value !== letter.value && !anyRemovedLetters && !anyGhostLetters;
    const willChangePosition = previewStateWord && previewLetter && previewIndex !== index && !anyRemovedLetters && !anyGhostLetters;
    
    return (
            <span 
                style={bouncing ? {animationDelay: `${index * 0.1}s`} : {}}
                className={`letter
                ${bouncing ? 'letter--bounce' : ''}
                ${willChangeValue ? 'letter--will-change-value' : ''}
                ${willChangePosition ? 'letter--will-change-position' : ''}
                ${willBeRemoved ? 'letter--will-be-removed' : ''}
                ${isGhost ? 'letter--ghost' : ''}
                ${!isGhost && wasGhost ? 'letter--was-ghost' : ''}
                ${wasChanged ? 'letter--was-changed' : ''}
            `}>{value}</span>
    );
}

interface MutatorItemProps{
    index: number
    mutator: GameMutator
    information: MutationResult|null // information[index]
    flipping: boolean // flippingMutators.includes(index)
    mutationStack: Mutator[]
    solutionLength: number,
    disableInput: boolean
    setPreviewMutator: React.Dispatch<React.SetStateAction<Mutator | null>>,
    onMutatorSelected: (m:GameMutator)=>void
    onUndo: ()=>void
}
function MutatorItem({
    flipping,
    information,
    mutationStack,
    mutator,
    index,
    solutionLength,
    disableInput,
    setPreviewMutator,
    onMutatorSelected,
    onUndo
}: MutatorItemProps){ 
    const stackIndex = mutationStack.findIndex(ms=>ms === mutator.mutator) 
    const x = index % 4 + (solutionLength === 1 ? 1.5 : 0);
    const y = Math.floor(index/4);
    function onMutatorClick() {
        if(disableInput) return;
        stackIndex !== -1 ? onUndo() : onMutatorSelected(mutator);
        setPreviewMutator(null)
    }
    const {ref, fontSize} = useAutoAdjustFontSize<HTMLDivElement>(16)

    const isHoverableDevice = window.matchMedia(
        '(hover: hover) and (pointer: fine)'
      ).matches;     
    return <LongPressable
        onLongPressEnd={()=>{
            if(isHoverableDevice || disableInput || stackIndex !== -1) return;
            setPreviewMutator(null)
        }}
        onLongPress={()=>{
            if(isHoverableDevice || disableInput || stackIndex !== -1) return;
            setPreviewMutator(mutator.mutator)
        }}
        onMouseEnter={()=>!disableInput && stackIndex === -1  && isHoverableDevice && setPreviewMutator(mutator.mutator)}
        onMouseLeave={()=>setPreviewMutator(null)}
        ref={ref}
    ><span
        className={`mutator
            ${information === MutationResult.Correct ? 'mutator--correct' : ''}
            ${information === MutationResult.WrongPlace ? 'mutator--wrong-place' : ''}
            ${information === MutationResult.Incorrect ? 'mutator--incorrect' : ''}  
            ${flipping ? 'mutator--flip' : ''}  
        `}
        onClick={onMutatorClick}
       key={mutator.mutator.name}
        style={stackIndex === -1 ? {
            left: `calc((var(--gap) + var(--w)) * ${x})`,
            top: `calc((var(--gap) + var(--w)) * ${y})`,
            fontSize: `${fontSize}px`,
        } : {
            top:  `calc(var(--w) * -1 - 15px)`,
            left:  solutionLength === 1 ? `calc((var(--gap) + var(--w)) * ${1.5})` : `calc((var(--gap) + var(--w)) * ${stackIndex})`,
            zIndex: stackIndex,
            fontSize: `${fontSize}px`
        }}
    >
        {mutator.mutator.name}
    </span></LongPressable>;
}
interface MutatorSlotProps {
    memory: Mutator|undefined
    solutionLength: number
    index: number
    flipping: boolean
}
function MutatorSlot({
    index,
    solutionLength,
    memory,
    flipping
}:MutatorSlotProps) {
    const [visibleMemory, setVisibleMemory] = useState<Mutator|undefined>(memory)
    const {ref, fontSize, reset} = useAutoAdjustFontSize<HTMLDivElement>(16)
    useEffect(()=>{
        reset()
    }, [visibleMemory, reset])
    const DELAY=300;
    useEffect(()=>{
        if(memory){
            const i = setTimeout(()=>{
                setVisibleMemory(memory)
            }, DELAY)
            return ()=>clearTimeout(i)
        }else{
            setVisibleMemory(undefined)
        }
    }, [memory])
    return <div ref={ref} key={`slot--${index}`} className={`mutator-slot ${index===0 ? 'first' : ''} ${index===solutionLength-1 ? 'last' : ''}`}style={{
        top:  `calc(var(--w) * -1 - 15px - var(--gap)/2)`,
        fontSize,
        left:  solutionLength === 1 ? `calc((var(--gap) + var(--w)) * ${1.5} - var(--gap)/2)` : `calc((var(--gap) + var(--w)) * ${index} - var(--gap)/2)`,
    }}>
        {!flipping && visibleMemory && visibleMemory.name}
    </div>
}